import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Link,
	useParams,
	useLocation
} from 'react-router-dom';
import {
	Header,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../services/utils';

import useForm from '../../../../../hooks/useForm';

import {
	getCalendarEvent
	// , updateCalendarEvent,
	// createCalendarEvent
} from '../../../../../api/nora/calendar';

import {
	useCalendarState,
	useCalendarAction
} from '../../../../../contexts/CalendarContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Alert from '../../../../Alert';
import AttendeesTab from './AttendeesTab';
import InfoTab from './InfoTab';

const EventDetails = () => {
	const { id } = useParams();
	const { state: navigationState } = useLocation();

	const {
		calendarEvent,
		initialized
	} = useCalendarState();
	const {
		setCalendarEvent,
		clearCalendarEvent,
		setEventTags
	} = useCalendarAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setNavLock
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( navigationState
		? navigationState.activeIndex
		: 0 );
	const [ unsavedProgressAlert, setUnsavedProgressAlert ] = useState( false );
	const [ eventSavedAlert, setEventSavedAlert ] = useState( false );
	const [ pendingIndex, setPendingIndex ] = useState();

	const {
		formData,
		hasChanged: formDataHasChanged,
		updateField,
		resetFormData,
		setFormData
	} = useForm();

	useEffect( () => {
		navigationState?.to
			? setInfobar( <span><Link to={ navigationState.to }>{ navigationState.text || '\u2190\u00A0\u00A0Back' }</Link></span> )
			: setInfobar( <span>Operations &nbsp;&#9656;&nbsp; <Link to="/operations/calendar">Calendar</Link> &nbsp;&#9656;&nbsp; { calendarEvent?.subject ? calendarEvent.subject : 'New Calendar Event' }</span> );
	}, [
		calendarEvent,
		navigationState,
		setInfobar
	] );

	useEffect( () => {
		if ( !initialized ) {
			setInfobarLoader( 'Please wait...' );
		} else {
			setEventTags();

			if ( id > 0 ) { // not a new calendar event
				( async () => {
					setInfobarLoader( 'Retrieving Event Info...' );

					try {
						const response = await getCalendarEvent( id );

						setCalendarEvent( response );
					} catch ( error ) {
						setApiErrorAlert( error );
					}
				} )();
			}
		}

		return () => {
			clearCalendarEvent();
		};
	}, [
		clearCalendarEvent,
		id,
		initialized,
		setApiErrorAlert,
		setCalendarEvent,
		setEventTags,
		setInfobarLoader
	] );

	useEffect( () => {
		if ( formDataHasChanged ) {
			setNavLock( true );
		} else {
			setNavLock( false );
		}
	}, [
		formDataHasChanged,
		setNavLock
	] );

	useEffect( () => {
		if ( calendarEvent ) { // if it is not a new calendarEvent
			const tags = calendarEvent.labels // labels on backend
				? calendarEvent.labels.map( ( { id } ) => id ) // labels on backend
				: [];

			setFormData( {
				...calendarEvent,
				title: calendarEvent.subject,
				location: calendarEvent.location,
				isOnlineMeeting: calendarEvent.isOnlineMeeting,
				onlineMeetingProvider: calendarEvent.onlineMeetingProvider,
				onlineMeetingUrl: calendarEvent.onlineMeetingUrl,
				start: calendarEvent.start,
				end: calendarEvent.end,
				description: calendarEvent.bodyPreview,
				tags,
				...navigationState && navigationState.initialData
			} );
		} else if ( id === '0' ) {
			setFormData( {
				id: 0,
				tagsReducers: [],
				...navigationState && navigationState.initialData
			} );
		}
	}, [
		calendarEvent,
		id,
		navigationState,
		setFormData
	] );

	useEffect( () => {
		if ( initialized && Object.keys( formData ).length ) {
			setIsLoading( false );
			setInfobarLoader( '' );
		}
	}, [
		formData,
		initialized,
		setInfobarLoader
	] );

	const errors = useMemo( () => ( {
		title: [
			{
				error: !formData.title,
				message: 'Title is a required field.',
				fieldLabel: 'Title'
			}
		]
	} ), [ formData.title ] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const handleSave = useCallback( /* async */ cb => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			// const labels = formData.tags.length // labels on backend
			// 	? formData.tags.reduce( ( acc, tag ) => {
			// 		return [ ...acc, { id: tag } ];
			// 	}, [] )
			// 	: [];

			// let { ...rest } = formData;

			// const data = {
			// 	...rest,
			// 	labels // labels on backend
			// };

			try {
				setNavLock( false );

				if ( formData.id === 0 ) {
					// const response = await createCalendarEvent( data );

					// setCalendarEvent( response );
				} else {
					// const response = await updateCalendarEvent( data );

					// setCalendarEvent( response );
				}

				setEventTags();
				// setCreatedTags( [] );
				setEventSavedAlert( true );

				cb?.();
			} catch ( error ) {
				setNavLock( true );
				setApiErrorAlert( error );
			}
		}
	}, [
		// createCalendarEvent,
		forceValidation,
		formData,
		setApiErrorAlert,
		// setCalendarEvent,
		setEventTags,
		setNavLock,
		// updateCalendarEvent,
		validate
	] );

	const menu = useMemo( () => ( {
		className: 'no-print',
		pointing: true,
		secondary: true,
		floated: true,
		style: {
			width: '100%',
			paddingTop: '0.5rem',
			marginBottom: '3rem'
		}
	} ), [] );

	const renderInfoTab = useCallback( () => (
		<InfoTab
			// createdTags={ createdTags }
			errors={ errors }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			locks={ navigationState?.lockedData || [] }
			resetFormData={ resetFormData }
			// setCreatedTags={ setCreatedTags }
			updateField={ updateField }
			validate={ validate }
		/>
	), [
		// createdTags,
		errors,
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		navigationState,
		resetFormData,
		updateField,
		validate
	] );

	const renderAttendeesTab = useCallback( () => <AttendeesTab />, [] );

	const renderPanes = useCallback( () => {
		let panes = [
			{
				menuItem: 'Info',
				render: renderInfoTab
			},
			{
				menuItem: 'Attendees',
				render: renderAttendeesTab
			}
		];

		return panes;
	}, [
		renderAttendeesTab,
		renderInfoTab
	] );

	const handleUnsavedProgress = useCallback( async () => {
		await handleSave();
		setUnsavedProgressAlert( false );
		setActiveIndex( pendingIndex );
	}, [
		handleSave,
		pendingIndex
	] );

	const closeUnsavedProgressAlert = useCallback( () => {
		setUnsavedProgressAlert( false );
	}, [] );

	const closeEventSavedAlert = useCallback( () => {
		setEventSavedAlert( false );
	}, [] );

	const handleTabChange = useCallback( ( e, { activeIndex } ) => {
		if ( !validate() ) {
			if ( forceValidation ) {
				setForceValidation( false );
			}

			if ( formDataHasChanged ) {
				setPendingIndex( activeIndex );
				setUnsavedProgressAlert( true );
			} else {
				setActiveIndex( activeIndex );
			}
		} else if ( !forceValidation ) {
			setForceValidation( true );
		}
	}, [
		forceValidation,
		formDataHasChanged,
		validate
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="detail-page">
			{ log( 'eventDetails' ) }
			<Tab
				activeIndex={ activeIndex }
				menu={ menu }
				onTabChange={ handleTabChange }
				panes={ renderPanes() }
			/>
			<Alert
				action={ handleUnsavedProgress }
				actionText="Save and continue"
				name="calendar-event-unsaved-progress-confirm-alert"
				message="Please save your progress before continuing."
				open={ unsavedProgressAlert }
				closeModal={ closeUnsavedProgressAlert }
				closeText="Go back"
				type="confirm"
			/>
			<Alert
				closeModal={ closeEventSavedAlert }
				message={ <Header as="h1" color="pink">Saved!</Header> }
				name="calendar-event-saved-notification"
				open={ eventSavedAlert }
				type="notification"
			/>
		</div>
	);
};

export default memo( EventDetails );
